import React from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Divider from '@amzn/meridian/divider';
import { useTranslation } from 'src/arb-wrapper';
import { type CampaignDesiredStateDto } from 'src/store/generated/leela-api-generated';
import { groupStrategiesByAbTesting, getProductSetsForGroup } from 'src/utils/abTestingUtils';
import { ProductSetDisplay } from 'src/components/campaigns/ProductSetDisplay';
import { ProductStrategyView } from 'src/components/campaigns/ProductStrategyView';

interface ProductStrategiesProps {
  campaign: CampaignDesiredStateDto;
}

export const ProductStrategies: React.FC<ProductStrategiesProps> = ({ campaign }) => {
  const { t } = useTranslation();

  if (!campaign.productStrategies || campaign.productStrategies.length === 0) {
    return null;
  }

  const { abTestingGroups, ungroupedStrategies } = groupStrategiesByAbTesting(campaign.productStrategies);

  return (
    <Column spacing="400">
      <Text type="h200">{t('product_strategies_header')}</Text>
      <Divider />

      <Column spacing="400">
        {/* Render A/B Testing Groups - only show group header for multiple strategies with same groupId */}
        {abTestingGroups.map((group) => {
          const productSets = getProductSetsForGroup(group.strategies);
          const { strategy } = group.strategies[0];
          const hasMultipleStrategiesInGroup = group.strategies.length > 1;

          return (
            <Column key={`ab-group-${group.groupId}`} spacing="300">
              {hasMultipleStrategiesInGroup && (
                <Row alignmentVertical="center" spacing="200">
                  <Text type="h300">
                    {t('ab_testing_group_header', { count: group.strategies.length })}
                  </Text>
                </Row>
              )}

              <div style={{ padding: '24px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <Column spacing="300">
                  {/* Product Sets Display - only show for multiple strategies in same group */}
                  {hasMultipleStrategiesInGroup && <ProductSetDisplay productSets={productSets} />}

                  <ProductStrategyView strategy={strategy} />
                </Column>
              </div>
            </Column>
          );
        })}

        {/* Render Ungrouped Strategies */}
        {ungroupedStrategies.map(({ strategy, index }) => (
          <div key={index} style={{ padding: '24px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <ProductStrategyView strategy={strategy} />
          </div>
        ))}
      </Column>
    </Column>
  );
};
